import { Button, Stack, Typography, Alert, Divider } from '@mui/material'
import RadioOption from 'components/atoms/input/RadioOption'
import QuestionOptionsTemplate, {
  QuestionOptionWrapper
} from 'components/templates/QuestionOptionsTemplate'
import StepQuizMatching from './StepQuizMatching'
import StepQuizSurveyResult from './StepQuizSurveyResult'
import {
  CONSTANTS_OPTIONS,
  GOAL_STEP_QUIZ_TYPES,
  USER_QUIZ_RESULTS_URL,
  SURVEY_HIGH_LOW_CUTOFF,
  GOAL_STEP_QUIZ_URL
} from 'services/constants'
import { useEffect, useState } from 'react'
import { getRequest, postRequest } from 'utils/api'
import { getVirtueById } from 'utils/getSupportingInfo'
import { getConstant } from 'services/constantServices'
import InCardStack from 'components/atoms/container/InCardStack'
import {
  deleteUserBasedStorageItem,
  getUserBasedStorageItem,
  setUserBasedStorageItem
} from 'utils/localStorage'
import RolePlayPageCard from 'components/organisms/rolePlay/RolePlayPageCard'
import { useTranslation } from 'react-i18next'
import StepQuizText from './StepQuizText'
import OverlayLoading from 'components/atoms/OverlayLoading'
import StepQuizCheck from './StepQuizCheck'

const quizAnswerBaseKey = 'quizAnswer'
const updatedQuizTypes = [
  GOAL_STEP_QUIZ_TYPES.CHECK,
  GOAL_STEP_QUIZ_TYPES.SURVEY,
  GOAL_STEP_QUIZ_TYPES.TEXT
]

const StepQuiz = ({
  goalStepId,
  editorMode = false,
  scrollToTop,
  setShowStepper,
  pathOpen,
  contentActionTracking,
  setDisableNextForRoleplay = () => {}
}) => {
  const { t } = useTranslation()
  const [stepData, setStepData] = useState(undefined)
  const [questions, setQuestions] = useState([])
  const [answers, setAnswers] = useState([])
  const [submitted, setSubmitted] = useState(false)
  const [options, setOptions] = useState([])
  const [quizResults, setQuizResults] = useState([])
  const [submitError, setSubmitError] = useState(false)
  const [errorMessage, setErrorMessage] = useState(undefined)
  const [initialLoading, setInitialLoading] = useState(false)
  const [loading, setLoading] = useState(false)

  const calculateSurveyScores = async (quest, ans) => {
    const scores = {}
    quest.questions.forEach((question, index) => {
      const attributeKey = question.virtue_id
      if (!scores[attributeKey]) {
        scores[attributeKey] = { sum: 0, count: 0 }
      }
      scores[attributeKey].sum += parseInt(ans[index])
      scores[attributeKey].count += 1
    })
    const resultArray = await Promise.all(
      Object.entries(scores).map(async ([key, value]) => {
        const intKey = parseInt(key)
        const virtue = await getVirtueById(intKey)

        const virtueMessages = quest.virtue_messages.find(
          x => x.virtue_id === intKey
        )

        const score = value.sum / value.count

        let highLowMsg
        let resultMessage
        if (virtueMessages?.low || virtueMessages?.high) {
          const low = virtueMessages?.low || null
          const high = virtueMessages?.high || null
          if (score > SURVEY_HIGH_LOW_CUTOFF && high) {
            highLowMsg = { type: 'high', message: high }
          } else if (score <= SURVEY_HIGH_LOW_CUTOFF && low) {
            highLowMsg = { type: 'low', message: low }
          } else {
            highLowMsg = null
          }
        } else if (virtueMessages?.messages?.length) {
          const msg = virtueMessages.messages.find(
            x => x.min < score && x.max >= score
          )
          if (msg) {
            resultMessage = msg.message
          }
        }

        return {
          name: virtue.name,
          score: score,
          description: virtue.description,
          highLowMsg,
          resultMessage
        }
      })
    )
    resultArray.sort((a, b) => b.score - a.score)
    setQuizResults(resultArray)
    setSubmitted(true)
  }

  useEffect(() => {
    const getQuizStepData = async () => {
      setLoading(true)
      const params = { goalStepId }
      if (editorMode) {
        params.editorMode = true
      }
      const res = await getRequest(GOAL_STEP_QUIZ_URL, params)

      if (res.status === 200) {
        setStepData(res.data.data)
      } else {
        setStepData(undefined)
      }
      setLoading(false)
    }
    getQuizStepData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goalStepId])

  const setAnswersFromLocalStorage = () => {
    const localQuizAnswers = getUserBasedStorageItem(
      quizAnswerBaseKey,
      stepData.goalStepId
    )
    if (localQuizAnswers) {
      setAnswers(localQuizAnswers)
      return localQuizAnswers
    }
  }

  useEffect(() => {
    const processData = async () => {
      setAnswers([])
      setInitialLoading(true)
      let quest
      try {
        quest = JSON.parse(stepData.questions)
      } catch {
        quest = stepData.questions
      }

      if (!stepData.userQuizResults?.length) {
        setSubmitted(false)
      }
      if (stepData.type === GOAL_STEP_QUIZ_TYPES.SURVEY) {
        if (Array.isArray(quest)) {
          // Required for backward compatiblity with old questions format
          quest = { questions: quest }
        }

        if (!quest.hasOwnProperty('virtue_messages')) {
          // Also for backward compatiblity
          quest.virtue_messages = []
        }

        const fetchedOptions = await getConstant(
          CONSTANTS_OPTIONS.JOURNEY_WORK_VALUE_SURVEY_OPTIONS
        )
        if (fetchedOptions) {
          setOptions(fetchedOptions)
        }

        if (stepData.userQuizResults?.length > 0) {
          await calculateSurveyScores(
            quest,
            stepData.userQuizResults[0].resultData
          )
        }
      } else if (stepData.type === GOAL_STEP_QUIZ_TYPES.TEXT) {
        if (stepData.userQuizResults?.length > 0) {
          setAnswers(stepData.userQuizResults[0].resultData)
          setSubmitted(true)
        } else {
          setAnswersFromLocalStorage()
        }
      } else if (stepData.type === GOAL_STEP_QUIZ_TYPES.ROLE_PLAY) {
        setDisableNextForRoleplay(stepData?.userQuizResults?.length === 0)
      }
      setQuestions(quest)
      setInitialLoading(false)
    }

    if (stepData) {
      processData()
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepData])

  const getHasUnAnswerQuestion = newAnswers => {
    return newAnswers.includes(undefined) || newAnswers.includes(null)
  }

  const handleAnswerChange = index => {
    return event => {
      const newAnswers = answers.length > 0 ? answers : Array(questions.length)
      newAnswers[index] = event.target.value
      let hasUnAnswerQuestion = getHasUnAnswerQuestion(newAnswers)
      setAnswers(newAnswers)
      setUserBasedStorageItem(
        quizAnswerBaseKey,
        stepData.goalStepId,
        newAnswers
      )

      if (!hasUnAnswerQuestion) {
        setSubmitError(false)
      }
    }
  }

  const handleSubmit = async () => {
    if (stepData.type !== GOAL_STEP_QUIZ_TYPES.TEXT) {
      scrollToTop()
    }
    const resultData =
      stepData.type === GOAL_STEP_QUIZ_TYPES.TEXT
        ? answers
        : answers.map(a => parseInt(a))
    const requestBody = { resultData, goalStepId: stepData.goalStepId }
    const res = await postRequest(USER_QUIZ_RESULTS_URL, requestBody)
    if (res.status === 201) {
      deleteUserBasedStorageItem(quizAnswerBaseKey, stepData.goalStepId)
    }
  }

  const onSubmitSurvey = async () => {
    await calculateSurveyScores(questions, answers)
    await handleSubmit()
  }

  const onSubmitText = async () => {
    await handleSubmit()
    setShowStepper(true)
    setSubmitted(true)
  }
  const onSubmitLearningCheck = async () => {
    const newAnswers = answers.length > 0 ? answers : Array(questions.length)
    let hasUnAnswerQuestion = getHasUnAnswerQuestion(newAnswers)
    if (!hasUnAnswerQuestion) {
      setLoading(true)
      if (stepData.type === GOAL_STEP_QUIZ_TYPES.SURVEY) {
        await onSubmitSurvey()
      } else if (stepData.type === GOAL_STEP_QUIZ_TYPES.TEXT) {
        await onSubmitText()
      } else {
        await handleSubmit()
      }

      setLoading(false)
      return true
    } else {
      setSubmitError(true)
      console.log('submitErrorHere', submitError)
      setErrorMessage(t('stepQuiz.answerAllQuestionsValidation'))
      return false
    }
  }
  const handleLearningCheckReset = () => {
    setAnswers(Array(questions.length))
    setSubmitted(false)
    setSubmitError(false)
    setUserBasedStorageItem(
      quizAnswerBaseKey,
      stepData.goalStepId,
      Array(questions.length)
    )
  }
  const SummaryText = ({ children }) => <Typography>{children}</Typography>

  const getOptions = (optionType, questionId) => {
    const selectedOptions = optionType ? options[optionType] : options[0]
    return selectedOptions?.map(v => (
      <RadioOption
        key={`option-${questionId}-${v.value}`}
        value={v.value}
        label={v.label_text}
        sx={{ gap: 2 }}
      />
    ))
  }
  return (
    <>
      {stepData && (
        <>
          {stepData.type === GOAL_STEP_QUIZ_TYPES.ROLE_PLAY ? (
            <RolePlayPageCard
              initialRolePlayId={stepData?.questions?.rolePlayId}
              onLearningPath={true}
              pathOpen={pathOpen}
              setDisableNextForRoleplay={setDisableNextForRoleplay}
            />
          ) : (
            <>
              {stepData.type !== GOAL_STEP_QUIZ_TYPES.CHECK && (
                <Divider width={'100%'} />
              )}
              <Stack
                sx={
                  stepData.type === GOAL_STEP_QUIZ_TYPES.CHECK
                    ? {
                        padding: 0,
                        paddingBottom: 8
                      }
                    : { paddingTop: 0 }
                }
                width='100%'>
                {updatedQuizTypes.includes(stepData.type) && !initialLoading ? (
                  <>
                    {stepData.type === GOAL_STEP_QUIZ_TYPES.TEXT ? (
                      <StepQuizText
                        stepData={stepData}
                        handleAnswerChange={handleAnswerChange}
                        submitted={submitted}
                        answers={answers}
                      />
                    ) : stepData.type === GOAL_STEP_QUIZ_TYPES.SURVEY ? (
                      <InCardStack
                        spacing={8}
                        alignItems={'flex-start'}
                        width={'100%'}>
                        <Typography variant='h3'>
                          {stepData.title || t('stepQuiz.workValuesSurvey')}
                          {submitted
                            ? ` ${quizResults.length === 1 ? t('stepQuiz.score') : t('stepQuiz.result')}`
                            : ''}
                        </Typography>
                        {questions.questions?.length ? (
                          submitted ? (
                            <>
                              {stepData.summaryEnd && (
                                <SummaryText>{stepData.summaryEnd}</SummaryText>
                              )}
                              <Divider width={'100%'} />
                              <StepQuizSurveyResult
                                resultData={quizResults}
                                quizData={stepData}
                                handleAnswerChange={handleAnswerChange}
                              />
                            </>
                          ) : (
                            <>
                              {stepData.summary && (
                                <SummaryText>{stepData.summary}</SummaryText>
                              )}
                              <Divider width={'100%'} />
                              {questions.questions?.map((q, i) => (
                                <QuestionOptionsTemplate
                                  key={`survey-question-${i}`}
                                  question={q.question}
                                  handleOnValueChange={handleAnswerChange(i)}
                                  submitError={submitError}
                                  options={getOptions(q.option_type, i)}
                                  width={'100%'}
                                  style={{
                                    paddingBottom:
                                      i < questions.questions?.length - 1
                                        ? 0
                                        : 16
                                  }}
                                />
                              ))}
                            </>
                          )
                        ) : null}
                      </InCardStack>
                    ) : stepData.type === GOAL_STEP_QUIZ_TYPES.CHECK ? (
                      <>
                        {stepData.summary && (
                          <SummaryText>{stepData.summary}</SummaryText>
                        )}
                        <StepQuizCheck
                          stepData={stepData}
                          questions={questions}
                          setShowStepper={setShowStepper}
                          contentActionTracking={contentActionTracking}
                          setAnswersFromLocalStorage={
                            setAnswersFromLocalStorage
                          }
                          answers={answers}
                          handleAnswerChange={handleAnswerChange}
                          onSubmitLearningCheck={onSubmitLearningCheck}
                          handleLearningCheckReset={handleLearningCheckReset}
                          scrollToTop={scrollToTop}
                          submitError={submitError}
                        />
                      </>
                    ) : null}
                    {!submitted &&
                    stepData.type !== GOAL_STEP_QUIZ_TYPES.CHECK ? (
                      <Button
                        onClick={onSubmitLearningCheck}
                        sx={{ width: 'fit-content', alignSelf: 'center' }}>
                        {t('checkInSurvey.submitAnswers')}
                      </Button>
                    ) : null}
                    {submitError ? (
                      <Alert severity='error' sx={{ marginTop: '10px' }}>
                        {errorMessage}
                      </Alert>
                    ) : null}
                  </>
                ) : null}

                {/* Old quiz types, to be removed after data cleanup complete */}
                {!updatedQuizTypes.includes(stepData.type) ? (
                  <Stack padding={0} spacing={5}>
                    <Typography variant='h4'>{stepData.summary}</Typography>
                    {stepData.type === 'true_false' ? (
                      <>
                        <Typography variant='h6' color='textSecondary'>
                          {t('stepQuiz.true')} {t('stepQuiz.or')}{' '}
                          {t('stepQuiz.false')}
                        </Typography>
                        {stepData.questions.map((q, i) => (
                          <QuestionOptionWrapper
                            key={`question-${i}`}
                            question={q.question}
                            correctAnswer={q.answer.toString()}
                            correctMessage={q.answerSummary}
                            options={
                              <>
                                <RadioOption
                                  value={'true'}
                                  label={t('stepQuiz.true')}
                                />
                                <RadioOption
                                  value={'false'}
                                  label={t('stepQuiz.false')}
                                />
                              </>
                            }
                          />
                        ))}
                      </>
                    ) : null}
                    {stepData.type === 'multiple_choice' ? (
                      <>
                        <Typography variant='h6' color='textSecondary'>
                          {t('stepQuiz.multipleChoice')}
                        </Typography>
                        {stepData.questions.map((q, i) => (
                          <QuestionOptionWrapper
                            key={`question-${i}`}
                            question={q.question}
                            correctAnswer={q.correctAnswerPosition.toString()}
                            correctMessage={t('stepQuiz.correct')}
                            options={q.answers.map((a, j) => (
                              <RadioOption
                                value={j}
                                label={a}
                                key={`option-${i}-${j}`}
                              />
                            ))}
                          />
                        ))}
                      </>
                    ) : null}
                    {stepData.type === 'matching' ? (
                      <StepQuizMatching questionData={stepData.questions} />
                    ) : null}
                  </Stack>
                ) : null}
              </Stack>
            </>
          )}
        </>
      )}

      <OverlayLoading open={loading} />
    </>
  )
}

export default StepQuiz
